import React, { Component } from 'react'
import '../styles/GlobalStyle.css'
import ColType from '../Types'
import { db } from '../firebase/Firebase'
import { flattenDoc, FOLDER_CURRENT } from '../Util'
import { Icon, Select } from 'antd'
import SpecialEducationCaseload from '../teacher/student/SpecialEducationCaseload'
import TeacherStudentReport from '../teacher/reports/TeacherStudentReport'
import PersonAvatar from '../customcomponents/PersonAvatar'
import CustomFooter from '../login/CustomFooter'

const NAV_WIDTH = 300

const SELECT_STYLE = { padding: '7px 28px 7px 10px', fontSize: 18, borderRadius: 8, border: '1px solid #e2e8f0', fontWeight: 600, color: '#1e293b', appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%2364748b' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center', backgroundSize: '12px', cursor: 'pointer' }

// NOTE: adjust the field order here if your school docs use a different name key.
function schoolDisplayName(s) {
    if (!s) return ''
    return s.schoolName || s.name || s.school || s.id
}

class MTSSAndIEP extends Component {
    state = {
        init: false,
        view: 'caseload',
        selectedSchoolId: null,
        selectedTeacherId: null,
        teachers: [],
        teachersDict: {},
        teachersLoading: false,
        students: [],
        studentDict: {},
        studentsLoading: false,
        // report view (MTSS & IEP Goals)
        selectedStudentId: null,
        selectedStudent: null,
        IEPGoals: [],
        IEPDict: {},
        iepLoading: false,
        iepStudentId: null, // which student the loaded IEPGoals belong to
    }

    componentDidMount() {
        document.title = 'MTSS and IEP - Dot It'
        this.getData()
    }

    // Base props (district + schools) can arrive after mount, so keep trying
    // until getData successfully initializes.
    componentWillReceiveProps() {
        this.getData()
    }

    // ---- base-prop helpers (support both admin and instructional facilitator) ----
    getSchools() {
        if (this.props.adminBaseProps && this.props.adminBaseProps.schools) return this.props.adminBaseProps.schools
        if (this.props.instructionalFacilitatorBaseProps && this.props.instructionalFacilitatorBaseProps.schools) return this.props.instructionalFacilitatorBaseProps.schools
        return null
    }

    getSortedSchools() {
        const s = this.getSchools()
        if (!s) return []
        return [...s].sort((a, b) => schoolDisplayName(a).localeCompare(schoolDisplayName(b)))
    }

    getDistrict() {
        if (this.props.adminBaseProps) return this.props.adminBaseProps.district
        if (this.props.instructionalFacilitatorBaseProps) return this.props.instructionalFacilitatorBaseProps.district
        return null
    }

    getDistrictId() {
        const d = this.getDistrict()
        return d ? d.id : null
    }

    getData() {
        if (this.state.init) return
        // wait for the district to be loaded
        if (this.props.adminBaseProps) {
            if (!(this.props.adminBaseProps.district && this.props.adminBaseProps.district.id)) return
        } else if (this.props.instructionalFacilitatorBaseProps) {
            const ifp = this.props.instructionalFacilitatorBaseProps
            if (!(ifp.district && ifp.district.id)) return
        } else {
            return
        }
        const schools = this.getSchools()
        if (schools == null) return // schools still loading
        this.setState({ init: true })
        if (schools.length === 0) return // nothing to select; render shows empty state
        const sid = this.getSortedSchools()[0].id
        this.setState({ selectedSchoolId: sid }, () => this.loadTeachers(sid))
    }

    // ---- teachers for the selected school ----
    loadTeachers = (schoolId) => {
        const districtId = this.getDistrictId()
        if (!districtId || !schoolId) { this.setState({ teachers: [], teachersDict: {}, teachersLoading: false }); return }
        this.setState({
            teachersLoading: true, teachers: [], teachersDict: {}, selectedTeacherId: null,
            students: [], studentDict: {},
            selectedStudentId: null, selectedStudent: null, IEPGoals: [], IEPDict: {}, iepStudentId: null,
        })
        const teachers = []
        const teachersDict = {}
        const addDocs = (qs) => {
            qs.forEach(doc => {
                const t = flattenDoc(doc)
                if (!teachersDict[t.id]) { teachersDict[t.id] = t; teachers.push(t) }
            })
        }

        db.collection(ColType.teacher)
            .where('districtId', '==', districtId)
            .where('schoolIds', 'array-contains', schoolId)
            .get()
            .then(addDocs)
            .catch(e => { console.log('MTSS teachers schoolIds query error (falling back to schoolId ==)', e) })
            .then(() => {
                // Runs whether the array-contains query returned empty OR was rejected.
                if (teachers.length > 0) return
                return db.collection(ColType.teacher)
                    .where('districtId', '==', districtId)
                    .where('schoolId', '==', schoolId)
                    .get()
                    .then(addDocs)
                    .catch(e => { console.log('MTSS teachers schoolId query error', e) })
            })
            .then(() => {
                if (this.state.selectedSchoolId !== schoolId) return // a newer school was picked
                teachers.sort((a, b) =>
                    (a.lastName || '').localeCompare(b.lastName || '') ||
                    (a.firstName || '').localeCompare(b.firstName || ''))
                const first = teachers.length ? teachers[0] : null
                this.setState({
                    teachers,
                    teachersDict,
                    teachersLoading: false,
                    selectedTeacherId: first ? first.id : null,
                }, () => { if (first) this.loadStudents(first) })
            })
            .catch(e => {
                console.log('MTSS load teachers error', e)
                if (this.state.selectedSchoolId !== schoolId) return
                this.setState({ teachers: [], teachersDict: {}, teachersLoading: false })
            })
    }

    // ---- students for the selected teacher ----
    loadStudents = (teacher) => {
        const districtId = this.getDistrictId()
        if (!teacher || !districtId) { this.setState({ students: [], studentDict: {}, studentsLoading: false }); return }
        const teacherId = teacher.id
        this.setState({
            studentsLoading: true, students: [], studentDict: {},
            selectedStudentId: null, selectedStudent: null, IEPGoals: [], IEPDict: {}, iepStudentId: null,
        })

        // Resolve the teacher's schools (single schoolId or a schoolIds array).
        let schoolIds = []
        if (teacher.hasOwnProperty('schoolId') && teacher.schoolId) schoolIds = [teacher.schoolId]
        if (teacher.hasOwnProperty('schoolIds') && teacher.schoolIds) schoolIds = teacher.schoolIds
        if (!schoolIds.length && this.state.selectedSchoolId) schoolIds = [this.state.selectedSchoolId]

        const students = []
        const studentDict = {}
        const promises = []
        schoolIds.forEach((schoolId) => {
            promises.push(new Promise((resolve) => {
                db.collection(ColType.student)
                    .where('teacherIds', 'array-contains', teacherId)
                    .where('schoolId', '==', schoolId)
                    .where('districtId', '==', districtId)
                    .where('folder', '==', FOLDER_CURRENT)
                    .get()
                    .then((querySnapshot) => {
                        querySnapshot.forEach((doc) => {
                            const student = flattenDoc(doc)
                            if (!studentDict[student.id]) { // de-dupe across overlapping schools
                                students.push(student)
                                studentDict[student.id] = student
                            }
                        })
                        resolve()
                    })
                    .catch(() => resolve())
            }))
        })

        Promise.all(promises)
            .then(() => {
                if (this.state.selectedTeacherId !== teacherId) return // a newer teacher was picked
                students.sort(this.compareStudents)
                const first = students.length ? students[0] : null
                this.setState({
                    students,
                    studentDict,
                    studentsLoading: false,
                    selectedStudentId: first ? first.id : null,
                    selectedStudent: first,
                }, () => this.maybeLoadReport())
            })
            .catch((e) => {
                console.log('MTSS load students error', e)
                if (this.state.selectedTeacherId !== teacherId) return
                this.setState({ students: [], studentDict: {}, studentsLoading: false })
            })
    }

    compareStudents = (a, b) => {
        const ga = a.grade === 'K' ? -1 : parseInt(a.grade, 10)
        const gb = b.grade === 'K' ? -1 : parseInt(b.grade, 10)
        const na = isNaN(ga) ? 999 : ga
        const nb = isNaN(gb) ? 999 : gb
        if (na !== nb) return na - nb
        return ((a.lastName || '') + '').localeCompare((b.lastName || '') + '')
    }

    // ---- IEP / MTSS goals for the selected student (report view) ----
    // Only load when we're on the report view and haven't already loaded this
    // student's goals; tracked by iepStudentId so switching views doesn't re-query.
    maybeLoadReport = () => {
        const { view, selectedStudent, selectedStudentId, iepStudentId, iepLoading } = this.state
        if (view !== 'report' || !selectedStudent || iepLoading) return
        if (iepStudentId === selectedStudentId) return
        this.loadIEPGoals(selectedStudent)
    }

    loadIEPGoals = (student) => {
        const districtId = this.getDistrictId()
        if (!student || !districtId) return
        const studentId = student.id
        this.setState({ iepLoading: true })
        db.collection(ColType.iep)
            .where('districtId', '==', districtId)
            .where('studentId', '==', studentId)
            .where('folder', '==', FOLDER_CURRENT)
            .get()
            .then(qs => {
                if (this.state.selectedStudentId !== studentId) return // student changed mid-flight
                const IEPGoals = []
                const IEPDict = {}
                qs.forEach(doc => { const iep = flattenDoc(doc); IEPGoals.push(iep); IEPDict[iep.id] = iep })
                this.setState({ IEPGoals, IEPDict, iepLoading: false, iepStudentId: studentId })
            })
            .catch(e => {
                console.log('MTSS load IEP goals error', e)
                if (this.state.selectedStudentId !== studentId) return
                this.setState({ IEPGoals: [], IEPDict: {}, iepLoading: false, iepStudentId: studentId })
            })
    }

    // ---- selection handlers ----
    onSchoolChange = (schoolId) => {
        if (schoolId === this.state.selectedSchoolId) return
        this.setState({
            selectedSchoolId: schoolId,
            selectedTeacherId: null,
            teachers: [],
            teachersDict: {},
            students: [],
            studentDict: {},
            selectedStudentId: null,
            selectedStudent: null,
            IEPGoals: [],
            IEPDict: {},
            iepStudentId: null,
        }, () => this.loadTeachers(schoolId))
    }

    onTeacherChange = (teacherId) => {
        if (teacherId === this.state.selectedTeacherId) return
        const teacher = this.state.teachersDict[teacherId] || null
        this.setState({
            selectedTeacherId: teacherId,
            students: [],
            studentDict: {},
            selectedStudentId: null,
            selectedStudent: null,
            IEPGoals: [],
            IEPDict: {},
            iepStudentId: null,
        }, () => { if (teacher) this.loadStudents(teacher) })
    }

    onStudentChange = (studentId) => {
        if (studentId === this.state.selectedStudentId) return
        const student = this.state.studentDict[studentId] || null
        this.setState({
            selectedStudentId: studentId,
            selectedStudent: student,
            IEPGoals: [],
            IEPDict: {},
        }, () => this.maybeLoadReport())
    }

    onSelectView = (key) => {
        this.setState({ view: key }, () => this.maybeLoadReport())
    }

    getSelectedTeacher() {
        return this.state.teachersDict[this.state.selectedTeacherId] || null
    }

    buildTeacherForProps(teacher) {
        return {
            ...teacher,
            schoolId: teacher.schoolId || this.state.selectedSchoolId,
            districtId: teacher.districtId || this.getDistrictId(),
        }
    }

    // ---- side navigation (school + teacher selectors, then the menu) ----
    renderSideNav() {
        const schools = this.getSortedSchools()
        const { teachers, selectedSchoolId, selectedTeacherId, teachersLoading } = this.state
        const groups = [
            { header: 'Special Education Caseload', items: [{ key: 'caseload', label: 'Special Education Caseload', icon: 'appstore' }] },
            { header: 'MTSS & IEP Goals', items: [{ key: 'report', label: 'MTSS & IEP Goals', icon: 'profile' }] },
        ]
        const labelStyle = { fontSize: 13, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.1, marginBottom: 8 }
        const subheaderStyle = { fontSize: 15, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.2, padding: '8px 14px 8px', marginBottom: 2 }

        return <div style={{ width: NAV_WIDTH, minWidth: NAV_WIDTH, flexShrink: 0, position: 'fixed', top: 84, left: 0, bottom: 0, backgroundColor: '#fff', borderRight: '1px solid #e2e8f0', padding: '20px 12px', zIndex: 150, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1.2, padding: '8px 14px 12px' }}>MTSS &amp; IEP</div>

            <div style={{ padding: '2px 14px 14px', marginBottom: 6, borderBottom: '2px solid #e2e8f0' }}>
                <div style={labelStyle}>School</div>
                <select value={selectedSchoolId || ''} onChange={e => this.onSchoolChange(e.target.value)} style={{ ...SELECT_STYLE, width: '100%', fontSize: 16, marginBottom: 14 }}>
                    {schools.length === 0 && <option value="" disabled>No schools available</option>}
                    {schools.map(s => <option key={s.id} value={s.id}>{schoolDisplayName(s)}</option>)}
                </select>

                <div style={labelStyle}>Teacher</div>
                <select value={selectedTeacherId || ''} onChange={e => this.onTeacherChange(e.target.value)} disabled={teachersLoading || teachers.length === 0} style={{ ...SELECT_STYLE, width: '100%', fontSize: 16 }}>
                    {teachersLoading && <option value="" disabled>Loading teachers…</option>}
                    {!teachersLoading && teachers.length === 0 && <option value="" disabled>No teachers at this school</option>}
                    {!teachersLoading && teachers.map(t => <option key={t.id} value={t.id}>{(t.lastName || '') + ', ' + (t.firstName || '')}</option>)}
                </select>
            </div>

            {groups.map((g, gi) => <React.Fragment key={g.header}>
                <div style={{ ...subheaderStyle, marginTop: gi === 0 ? 0 : 10 }}>{g.header}</div>
                {g.items.map(t => {
                    const active = this.state.view === t.key
                    return <button key={t.key} onClick={() => this.onSelectView(t.key)} style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '11px 14px', fontSize: 17, fontWeight: active ? 800 : 600, border: 'none', borderRadius: 10, cursor: 'pointer', backgroundColor: active ? '#eff6ff' : 'transparent', color: active ? '#1e40af' : '#475569', textAlign: 'left', marginBottom: 2 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, flex: '0 0 34px', backgroundColor: active ? '#2d6a9f' : '#f0f2f5', color: active ? '#fff' : '#475569' }}>
                            <Icon type={t.icon} style={{ fontSize: 17 }} />
                        </span>
                        {t.label}
                    </button>
                })}
            </React.Fragment>)}
        </div>
    }

    renderCenter(content) {
        return <div style={{ marginLeft: NAV_WIDTH, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 160px)', padding: '40px 24px', fontSize: 20, fontWeight: 700, color: '#64748b' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>{content}</div>
        </div>
    }

    // ---- Special Education Caseload view ----
    renderCaseloadView(teacher) {
        const teacherForProps = this.buildTeacherForProps(teacher)
        const teacherBaseProps = {
            teacher: teacherForProps,
            students: this.state.students,
            studentDict: this.state.studentDict,
            district: this.getDistrict(),
        }
        // key on the teacher id so the caseload fully remounts (and reloads that
        // teacher's saved impact grid) whenever the selected teacher changes.
        return <SpecialEducationCaseload
            key={teacher.id}
            teacherBaseProps={teacherBaseProps}
            leftOffset={NAV_WIDTH}
            readOnly={true}
        />
    }

    // ---- MTSS & IEP Goals view (student dropdown + TeacherStudentReport) ----
    renderReportView(teacher) {
        const { students, studentsLoading, selectedStudentId, selectedStudent } = this.state
        const teacherForProps = this.buildTeacherForProps(teacher)

        // Mirror the prop shape AdminStudentView hands TeacherStudentReport.
        const reportProps = {
            schoolId: this.state.selectedSchoolId,
            teacherId: this.state.selectedTeacherId,
            teacher: teacherForProps,
            studentId: selectedStudentId,
            student: selectedStudent,
            students: students,
            studentDict: this.state.studentDict,
            IEPGoals: this.state.IEPGoals,
            IEPDict: this.state.IEPDict,
            district: this.getDistrict(),
        }

        return <div className='mt-100' style={{ marginLeft: NAV_WIDTH, padding: '24px', display: 'flex', justifyContent: 'center' }}>
            <div style={{ flex: 1, maxWidth: 1100, width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginLeft: 38, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Student</span>
                    {studentsLoading
                        ? <span style={{ fontSize: 16, color: '#64748b' }}><Icon type="loading" className="mr-1" />Loading students…</span>
                        : students.length === 0
                            ? <span style={{ fontSize: 16, color: '#64748b' }}>No students for this teacher.</span>
                            : <Select
                                showSearch
                                value={selectedStudentId || undefined}
                                placeholder="Select a student"
                                style={{ width: 360 }}
                                size="large"
                                optionFilterProp="children"
                                onChange={this.onStudentChange}
                                filterOption={(input, option) => {
                                    if (!input) return true
                                    const person = option.props.children.props.person
                                    const fullName = (person.firstName + ' ' + person.lastName).toLowerCase()
                                    return fullName.includes(input.toLowerCase())
                                }}
                            >
                                {students.map(s => <Select.Option value={s.id} key={s.id}>
                                    <PersonAvatar person={s} containerClassName="font-16" avatarClassName="font-16 font-bold mr-2" personClassName="font-black font-16" />
                                </Select.Option>)}
                            </Select>}
                </div>

                <div className='mb-100'>
                    {this.state.iepLoading ?
                        <div className='flex flex-v-center flex-h-center w-100 mt-100'>
                            <Icon type="loading" className='font-20' />
                            <span className='ml-2 font-20'>Loading...</span>
                        </div> :
                        !studentsLoading && students.length > 0 && selectedStudent
                            // key on the student id so the report remounts on each student change
                            ? <TeacherStudentReport
                                key={selectedStudentId}
                                teacherBaseProps={reportProps}
                                showStudentNav={false}
                                showFooter={false}
                                noMarginTop={true}
                                isAdmin={true}
                            />
                            : (!studentsLoading && students.length > 0
                                ? <div style={{ padding: '40px 24px', textAlign: 'center', color: '#94a3b8', fontSize: 18 }}>Select a student to see their MTSS &amp; IEP goals.</div>
                                : null)}
                </div>
                <CustomFooter />
            </div>
        </div>
    }

    renderMain() {
        const schools = this.getSchools()
        const teacher = this.getSelectedTeacher()

        if (schools == null) return this.renderCenter(<div><Icon type="loading" style={{ fontSize: 30, color: '#3b82f6', marginRight: 10 }} />Loading…</div>)
        if (schools.length === 0) return this.renderCenter(<div>No schools are available for your account yet.</div>)
        if (this.state.teachersLoading) return this.renderCenter(<div><Icon type="loading" style={{ fontSize: 30, color: '#3b82f6', marginRight: 10 }} />Loading teachers…</div>)
        if (this.state.teachers.length === 0) return this.renderCenter(<div>There are no teachers at this school.</div>)
        if (!teacher) return this.renderCenter(<div>Select a teacher to continue.</div>)

        if (this.state.view === 'report') return this.renderReportView(teacher)
        return this.renderCaseloadView(teacher)
    }

    render() {
        return <div style={{ fontSize: 18 }}>
            {this.renderSideNav()}
            {this.renderMain()}
        </div>
    }
}

export default MTSSAndIEP